import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Radio } from '@creditornot/cb-radio';
import { useMutation } from '@tanstack/react-query';
import R from 'ramda';
import React from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkEditPayload, Category, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { WoltModal } from 'common/components';
import {
  EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY,
  fromWeeklyScheduleInputEntriesToWeeklyTimeSpans,
  WeeklyScheduleInput,
  WeeklyScheduleInputEntry,
} from 'common/components/weekly-schedules';
import validateYupSchema from 'common/utils/validateYupSchema';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useBulkChangeVisibilityAndAvailabilityTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type FormValues = {
  useWeeklyAvailability: boolean;
  useWeeklyVisibility: boolean;
  weeklyAvailability: WeeklyScheduleInputEntry[];
  weeklyVisibility: WeeklyScheduleInputEntry[];
};

type Props = {
  categories: Category[];
  menuId: string;
  onClearSelection: () => void;
  onClose: () => void;
  selectedIds: Set<string>;
};

const Root = styled(WoltModal)`
  width: calc(100vw - 40px);
  min-width: 480px;
  max-width: 640px;
`;

const StyledModalHeader = styled(ModalHeader)`
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
  overflow-y: scroll;
  max-height: 80vh;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WeeklyScheduleWrapper = styled.div`
  max-width: 392px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding-block-start: 24px;
  margin-block-start: 24px;
`;

const StyledRadio = styled(Radio)`
  & + & {
    margin-block-start: 12px;
  }
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: ${cbSemanticCssVars.surfaceMain};
`;

const CancelButton = styled(Button).attrs({ variant: 'lightBlue', size: 'small' })`
  margin-inline-end: 16px;
`;
const SubmitButton = styled(Button).attrs({ variant: 'blue', size: 'small' })``;

const schema = yup
  .object({
    weeklyAvailability: yup.mixed<FormValues['weeklyAvailability']>().required(),
    weeklyVisibility: yup.mixed<FormValues['weeklyVisibility']>().required(),
  })
  .required();

const initialFormValues = {
  useWeeklyAvailability: false,
  useWeeklyVisibility: false,
  weeklyAvailability: [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
  weeklyVisibility: [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
};

const BulkAvailabilityVisibilityChangeModal = ({
  categories,
  menuId,
  onClearSelection,
  onClose,
  selectedIds,
}: Props) => {
  const intl = useIntl();
  const { updateMenuData } = useMenuData();
  const trackChanges = useBulkChangeVisibilityAndAvailabilityTracking([...selectedIds]);
  const handleAvailabilityVisibilityChange = useMutation((payload: BulkEditPayload) => bulkEditItems(menuId, payload), {
    onMutate: () => {
      reportBulkAction(selectedIds.size, 'BULK_CHANGE_AVAILABILITY_VISIBILITY');
    },
    onSuccess: (data: MenuContainer, variables: BulkEditPayload) => {
      updateMenuData({ actionType: 'updateMenuContainer', payload: data });
      onClearSelection();
      trackChanges(variables.weekly_state?.weekly_availability, variables.weekly_state?.weekly_visibility);
      toast.success(<FormattedMessage id="menu.bulkActions.availabilityAndVisibilityChange.successDescription" />);
    },
    onError: () => {
      toast.error(<FormattedMessage id="menu.bulkActions.availabilityAndVisibilityChange.errorDescription" />, {
        duration: Infinity,
      });
    },
    onSettled: () => {
      onClose();
    },
  });

  return (
    <Root isOpen onRequestClose={onClose}>
      <StyledModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.availabilityAndVisibilityChange.title" />}
        />
      </StyledModalHeader>
      <Form<FormValues>
        initialValues={initialFormValues}
        onSubmit={values => {
          const weeklyAvailability =
            values.useWeeklyAvailability && values.weeklyAvailability.length > 0
              ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(values.weeklyAvailability)
              : null;
          const weeklyVisibility =
            values.useWeeklyVisibility && values.weeklyVisibility.length > 0
              ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(values.weeklyVisibility)
              : null;

          const payload: BulkEditPayload = {
            ...buildBulkActionBasePayload(categories, selectedIds),
            weekly_state: {
              weekly_availability: weeklyAvailability && weeklyAvailability.length > 0 ? weeklyAvailability : null,
              weekly_visibility: weeklyVisibility && weeklyVisibility.length > 0 ? weeklyVisibility : null,
            },
          };

          handleAvailabilityVisibilityChange.mutate(payload);
        }}
        validate={validateYupSchema(schema)}
      >
        {({ handleSubmit, hasValidationErrors, values }) => (
          <form onSubmit={handleSubmit}>
            <StyledModalContent>
              <FormRow label={intl.formatMessage({ id: 'menu-item.time-available' })} horizontalPadding>
                <ColumnContainer>
                  <Field<FormValues['useWeeklyAvailability']> name="useWeeklyAvailability">
                    {({ input }) => (
                      <RadioWrapper>
                        <StyledRadio checked={!input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-available-always" />
                        </StyledRadio>
                        <StyledRadio checked={input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-available-sometimes" />
                        </StyledRadio>
                      </RadioWrapper>
                    )}
                  </Field>

                  {values.useWeeklyAvailability && (
                    <Field<FormValues['weeklyAvailability']> name="weeklyAvailability" isEqual={R.equals}>
                      {({ input, meta }) => (
                        <WeeklyScheduleWrapper>
                          <WeeklyScheduleInput
                            onChange={input.onChange}
                            pristine={!!meta.pristine}
                            select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                            value={input.value}
                          />
                        </WeeklyScheduleWrapper>
                      )}
                    </Field>
                  )}
                </ColumnContainer>
              </FormRow>

              <FormRow label={intl.formatMessage({ id: 'menu-item.time-visible' })} horizontalPadding>
                <ColumnContainer>
                  <Field<FormValues['useWeeklyVisibility']> name="useWeeklyVisibility">
                    {({ input }) => (
                      <RadioWrapper>
                        <StyledRadio checked={!input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-visible-always" />
                        </StyledRadio>
                        <StyledRadio checked={input.value} onChange={() => input.onChange(!input.value)}>
                          <FormattedMessage id="menu-item.time-visible-sometimes" />
                        </StyledRadio>
                      </RadioWrapper>
                    )}
                  </Field>

                  {values.useWeeklyVisibility && (
                    <Field<FormValues['weeklyVisibility']> name="weeklyVisibility" isEqual={R.equals}>
                      {({ input, meta }) => (
                        <WeeklyScheduleWrapper>
                          <WeeklyScheduleInput
                            onChange={input.onChange}
                            pristine={!!meta.pristine}
                            select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                            value={input.value}
                          />
                        </WeeklyScheduleWrapper>
                      )}
                    </Field>
                  )}
                </ColumnContainer>
              </FormRow>
            </StyledModalContent>
            <StyledModalFooter>
              <CancelButton onClick={onClose}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SubmitButton
                disabled={hasValidationErrors || handleAvailabilityVisibilityChange.isLoading}
                loading={handleAvailabilityVisibilityChange.isLoading}
                type="submit"
                onClick={handleSubmit}
              >
                <FormattedMessage id="state.save" />
              </SubmitButton>
            </StyledModalFooter>
          </form>
        )}
      </Form>
    </Root>
  );
};

export default BulkAvailabilityVisibilityChangeModal;
